/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import * as default_data from '../../config.js'
import * as log from 'loglevel'
import { getToken } from '../../AuthService.js';
import { proposeModelCount, getTokenizerR } from '../../Utils/GenAI_helper.js'

async function getFileContent(file) {
  const token = getToken();
  if (!token) {
    return false;
  }

  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(`${default_data.default_chat_service_url_files}/file`, {
      method: 'POST',
      headers: {
        'x-access-tokens': token,
        'Connection': 'keep-alive'
      },
      body: formData,
      timeout: 1200000,
    });

    const data = await response.json();
    let ret = false;

    if(data['status'] == '200') {
      if(typeof data['ret'] === 'object') {
        if('text' in data['ret']) {
          ret = data['ret'].text;
        }
        else {
          ret = false;
        }
      }
      else {
        ret = data['ret']
      }
    }  
    else if(data['response'] === 'No file too large') {
      ret = false;
    }           
    else if(data['response'] === 'Virus detected in file') {
      ret = false;
    }           
    else if(data['response'] === 'File type not allowed') {
      ret = false;
    }
    return ret;
  }
  catch (error) {
    log.error('source: getFileContent:', error.message);
    return false;
  }
}

async function getURLContent(url) {
  const token = getToken();
  if (!token) {
    return false;
  }

  var url_pattern = '(https?://(?:www\.)?[a-zA-Z0-9-.]+\.[a-zA-Z]{2,}(?:/[^\s\'"<]*)?)'
  var url_match = url.match(url_pattern);
  if(url_match === null || url_match === undefined || url_match.length === 0) {
    return false;
  }

  var prompt_template = '[[SUMMARIZE_WEB]] ' + url + ' @@@|@@@ ' + 'Provide the entire content of the provided text coming from a HTML page' 

  try {
    const formData = new FormData();
    formData.append('message', prompt_template);
    formData.append('model', await proposeModelCount(70000));
    formData.append('dataset', "none");
    formData.append('live', 0);
    formData.append('streaming', false);

    const response = await fetch(`${default_data.default_chat_service_url_files}/query`, {
      method: 'POST',
      headers: {
        'x-access-tokens': token,
        'Connection': 'keep-alive'
      },
      body: formData,
      timeout: 1200000,
    });

    const data = await response.json();
    if(data['status'] === 200) {
      return data['message'];
    }
    else {
      return false;
    }
  }
  catch (error) {
    log.error('getURLContent: Error:', error.message);
    return false;
  }
}

async function getSummary(content) {
  const token = getToken();
  if (!token) {
    return false;
  }

  var prompt_template = "Write a detailed summary of the provided text, focusing on the main points. Do not include any introductory phrases. Never use phrases like \"the text is about\". Ensure the summary is concise and effectively communicates the key points of the content:\n" + content;

  try {
    const formData = new FormData();
    formData.append('message', prompt_template);
    formData.append('model', await proposeModelCount(await getTokenizerR(prompt_template)));
    formData.append('dataset', "none");
    formData.append('live', 0);
    formData.append('streaming', false);

    const response = await fetch(`${default_data.default_chat_service_url}/query`, {
      method: 'POST',
      headers: {
        'x-access-tokens': token,
        'Connection': 'keep-alive'
      },
      body: formData,
      timeout: 1200000,
    });

    const data = await response.json();
    if(data['status'] === 200) {
      return data['message'];
    }
    else {
      return false;
    }
  }
  catch (error) {
    log.error('getSummary: Error:', error.message);
    return false;
  }
}

async function addSource(workbook_id, source_type, identifier) {
  try {

    var token = getToken()
    if(token === false) {
      return {'response': 'Please login again', 'status': 400};
    }

    var file_content = '';
    var final_identifier = identifier;

    if(source_type === 'url') {
      file_content = await getURLContent(identifier);
      final_identifier = identifier;
    }
    else if(source_type === 'filename') {
      // pull file
      file_content = await getFileContent(identifier);
      if(file_content === false || file_content === null || file_content === '' || file_content === undefined) {
        return {'response': 'Error pulling file content', 'status': 400};
      }
      // if identifier['path'] exists use that otherwise use JSON.stringify(identifier)
      if(identifier['path'] === null || identifier['path'] === '' || identifier['path'] === undefined) {
        identifier['path'] = JSON.stringify(identifier);
      }
      final_identifier = identifier['path'];
    }
    else {
      // get 100 characters from identifier as identifier and save full identifier as file_content
      if(identifier.length > 100) {
        file_content = identifier;
        final_identifier = identifier.substring(0, 100);
      }
      else {
        file_content = identifier;
        final_identifier = identifier;
      }
    }

    var summary = await getSummary(file_content);
    if(summary === false || summary === null || summary === '' || summary === undefined) {
      summary = '';
    }

    if(final_identifier.startsWith('./')) {
      // remove ./
      final_identifier = final_identifier.substring(2);
    }

    const response = await fetch(default_data.default_user_service_url_no_cac + '/source', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-access-tokens': `${token}`
        },
        body: JSON.stringify({
          workbook_id: workbook_id,
          source_type: source_type,
          identifier: final_identifier,
          full_text: file_content,
          summary: summary,
          // tags empty for now
          tags: ''
        })
      })
      
      const data = await response.json();
      if(data.status != 200) {
        return {'response': data.response, 'status': 400};
      }
      return {'response': 'OK', 'status': 200};
    }
    catch (error) {
      log.error('addSource: Error:', error.message);
      return {'response': 'An error occurred while getting data.', 'status': 400};
    }
  };

  function getFilteredSourceContent(selectedSources) {

    // list through dictionary and get key and object
    var sources = [];
    for (var key in selectedSources) {
      if (selectedSources.hasOwnProperty(key)) {
        sources.push(selectedSources[key]);
      }
    }

    var ret = [];
    for(var i = 0; i < sources.length; i++) {
      var section = i + 1;
      ret.push(sources[i].full_text);
    }
    // return aggregated full_text into string separated by \n
    return ret.join('\n');    
  }

  async function deleteSource(source_id) {
    try {
  
      var token = getToken()
      if(token === false) {
        return {'response': 'Please login again', 'status': 400};
      }
  
      const response = await fetch(default_data.default_user_service_url_no_cac + '/source', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'x-access-tokens': `${token}`
          },
          body: JSON.stringify({
            source_id: source_id
          })
        })
        
        const data = await response.json();
        if(data.status != 200) {
          return {'response': data.response, 'status': 400};
        }
        return {'response': 'OK', 'status': 200};
      }
      catch (error) {
        log.error('deleteSource: Error:', error.message);
        return {'response': 'An error occurred while deleting data.', 'status': 400};
      }
    };
  
  export { addSource, deleteSource, getFilteredSourceContent, getURLContent};